import { supabase } from '../lib/supabase';

/**
 * Enhanced Accountant Tools Service
 * Handles all accounting tool operations with comprehensive error handling and RLS policy compatibility
 */
export const accountantToolsService = {
  // Enhanced error handler
  _handleError(error, operation) {
    console.error(`AccountantToolsService - ${operation}:`, error);
    
    // Categorize errors for better handling
    if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
      throw new Error(`Authentication required for ${operation}. Please sign in again.`);
    }
    
    if (error?.message?.includes('403') || error?.message?.includes('Forbidden')) {
      throw new Error(`Insufficient permissions for ${operation}. Contact your administrator.`);
    }
    
    if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
      throw new Error(`Network error during ${operation}. Please check your connection.`);
    }
    
    throw new Error(`${operation} failed: ${error?.message || 'Unknown error'}`);
  },

  // Helper to get current user safely
  async _getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase?.auth?.getUser();
      if (error || !user) {
        throw new Error('User not authenticated');
      }
      return user;
    } catch (error) {
      throw new Error('Authentication verification failed');
    }
  },

  // Journal Entries Operations - Fixed
  async getJournalEntries(organizationId, options = {}) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { limit = 50, offset = 0, status, dateFrom, dateTo } = options;
      
      // Build query without optional chaining to prevent breaking the chain
      let query = supabase?.from('journal_entries')?.select(`
          id,
          entry_number,
          entry_date,
          description,
          reference_number,
          status,
          total_debit,
          total_credit,
          created_at,
          created_by,
          posted_at,
          posted_by,
          reviewed_at,
          reviewed_by,
          created_by_profile:user_profiles!created_by(
            full_name, 
            email
          )
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      // Apply filters first
      if (status) {
        query = query?.eq('status', status);
      }

      if (dateFrom) {
        query = query?.gte('entry_date', dateFrom);
      }

      if (dateTo) {
        query = query?.lte('entry_date', dateTo);
      }

      // Apply pagination using range method only
      if (limit && typeof limit === 'number' && offset && typeof offset === 'number') {
        query = query?.range(offset, offset + limit - 1);
      } else if (limit && typeof limit === 'number') {
        query = query?.limit(limit);
      }

      const { data, error } = await query;

      if (error) {
        this._handleError(error, 'fetch journal entries');
      }

      return data || [];
    } catch (error) {
      this._handleError(error, 'get journal entries');
    }
  },

  async createJournalEntry(organizationId, journalData) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const currentUser = await this._getCurrentUser();
      const { lines, ...entryData } = journalData;
      
      // Create journal entry
      const { data: entry, error: entryError } = await supabase?.from('journal_entries')?.insert({
          ...entryData,
          organization_id: organizationId,
          created_by: currentUser?.id,
          status: 'draft'
        })?.select()?.single();

      if (entryError) {
        this._handleError(entryError, 'create journal entry');
      }

      // Create journal entry lines if provided
      if (lines?.length && entry?.id) {
        const { error: linesError } = await supabase?.from('journal_entry_lines')?.insert(
            lines?.map((line, index) => ({
              ...line,
              journal_entry_id: entry?.id,
              line_order: index + 1
            }))
          );

        if (linesError) {
          this._handleError(linesError, 'create journal entry lines');
        }
      }

      return entry;
    } catch (error) {
      this._handleError(error, 'create journal entry');
    }
  },

  async updateJournalEntryStatus(entryId, status, notes = null) {
    try {
      if (!entryId || !status) {
        throw new Error('Entry ID and status are required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const currentUser = await this._getCurrentUser();
      const updateData = {
        status,
        ...(notes && { notes }),
        updated_at: new Date()?.toISOString()
      };

      if (status === 'posted') {
        updateData.posted_at = new Date()?.toISOString();
        updateData.posted_by = currentUser?.id;
      }

      if (status === 'approved') {
        updateData.reviewed_at = new Date()?.toISOString();
        updateData.reviewed_by = currentUser?.id;
      }

      const { data, error } = await supabase?.from('journal_entries')?.update(updateData)?.eq('id', entryId)?.select()?.single();

      if (error) {
        this._handleError(error, 'update journal entry status');
      }

      return data;
    } catch (error) {
      this._handleError(error, 'update journal entry status');
    }
  },

  // Bulk Operations - Fixed
  async getBulkOperations(organizationId, options = {}) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { limit = 20, status, operationType } = options;
      
      let query = supabase?.from('bulk_operations')?.select(`
          id,
          operation_type,
          description,
          status,
          records_total,
          records_processed,
          records_failed,
          error_details,
          created_at,
          completed_at,
          created_by,
          created_by_profile:user_profiles!created_by(
            full_name, 
            email
          )
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (limit && typeof limit === 'number') {
        query = query?.limit(limit);
      }

      if (status) {
        query = query?.eq('status', status);
      }

      if (operationType) {
        query = query?.eq('operation_type', operationType);
      }

      const { data, error } = await query;

      if (error) {
        this._handleError(error, 'fetch bulk operations');
      }

      return data || [];
    } catch (error) {
      this._handleError(error, 'get bulk operations');
    }
  },

  async createBulkOperation(organizationId, operationData) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const currentUser = await this._getCurrentUser();

      const { data, error } = await supabase?.from('bulk_operations')?.insert({
          ...operationData,
          organization_id: organizationId,
          created_by: currentUser?.id,
          status: 'pending'
        })?.select()?.single();

      if (error) {
        this._handleError(error, 'create bulk operation');
      }

      return data;
    } catch (error) {
      this._handleError(error, 'create bulk operation');
    }
  },

  // Chart of Accounts Operations - Fixed
  async getChartOfAccounts(organizationId, options = {}) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { accountType, status = 'active', includeInactive = false } = options;
      
      let query = supabase?.from('chart_of_accounts')?.select(`
          id,
          account_code,
          account_name,
          account_type,
          account_subtype,
          normal_balance,
          status,
          description,
          parent_account_id,
          is_system_account,
          created_at
        `)?.eq('organization_id', organizationId)?.order('account_code');

      if (!includeInactive) {
        query = query?.eq('status', status);
      }

      if (accountType) {
        query = query?.eq('account_type', accountType);
      }

      const { data, error } = await query;

      if (error) {
        this._handleError(error, 'fetch chart of accounts');
      }

      return data || [];
    } catch (error) {
      this._handleError(error, 'get chart of accounts');
    }
  },

  async createAccount(organizationId, accountData) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const currentUser = await this._getCurrentUser();

      const { data, error } = await supabase?.from('chart_of_accounts')?.insert({
          ...accountData,
          organization_id: organizationId,
          created_by: currentUser?.id
        })?.select()?.single();

      if (error) {
        this._handleError(error, 'create account');
      }

      return data;
    } catch (error) {
      this._handleError(error, 'create account');
    }
  },

  // Tax Adjustments Operations - Fixed
  async getTaxAdjustments(organizationId, options = {}) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { limit = 50, adjustmentType, dateFrom, dateTo } = options;
      
      let query = supabase?.from('tax_adjustments')?.select(`
          id,
          adjustment_type,
          description,
          adjustment_date,
          amount,
          currency,
          tax_period,
          status,
          created_at,
          approved_at,
          created_by,
          approved_by,
          created_by_profile:user_profiles!created_by(
            full_name, 
            email
          )
        `)?.eq('organization_id', organizationId)?.order('adjustment_date', { ascending: false });

      if (limit && typeof limit === 'number') {
        query = query?.limit(limit);
      }

      if (adjustmentType) {
        query = query?.eq('adjustment_type', adjustmentType);
      }

      if (dateFrom) {
        query = query?.gte('adjustment_date', dateFrom);
      }

      if (dateTo) {
        query = query?.lte('adjustment_date', dateTo);
      }

      const { data, error } = await query;

      if (error) {
        this._handleError(error, 'fetch tax adjustments');
      }

      return data || [];
    } catch (error) {
      this._handleError(error, 'get tax adjustments');
    }
  },

  async createTaxAdjustment(organizationId, adjustmentData) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const currentUser = await this._getCurrentUser();

      const { data, error } = await supabase?.from('tax_adjustments')?.insert({
          ...adjustmentData,
          organization_id: organizationId,
          created_by: currentUser?.id
        })?.select()?.single();

      if (error) {
        this._handleError(error, 'create tax adjustment');
      }

      return data;
    } catch (error) {
      this._handleError(error, 'create tax adjustment');
    }
  },

  // Currency Adjustments Operations - Fixed
  async getCurrencyAdjustments(organizationId, options = {}) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { limit = 50, adjustmentType, currency } = options;
      
      let query = supabase?.from('currency_adjustments')?.select(`
          id,
          adjustment_type,
          description,
          adjustment_date,
          foreign_currency,
          exchange_rate,
          functional_amount,
          foreign_amount,
          unrealized_gain_loss,
          created_at,
          created_by,
          created_by_profile:user_profiles!created_by(
            full_name, 
            email
          )
        `)?.eq('organization_id', organizationId)?.order('adjustment_date', { ascending: false });

      if (limit && typeof limit === 'number') {
        query = query?.limit(limit);
      }

      if (adjustmentType) {
        query = query?.eq('adjustment_type', adjustmentType);
      }

      if (currency) {
        query = query?.eq('foreign_currency', currency);
      }

      const { data, error } = await query;

      if (error) {
        this._handleError(error, 'fetch currency adjustments');
      }

      return data || [];
    } catch (error) {
      this._handleError(error, 'get currency adjustments');
    }
  },

  // Period Locks Operations - Fixed
  async getPeriodLocks(organizationId, options = {}) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { lockStatus, lockType } = options;
      
      let query = supabase?.from('period_locks')?.select(`
          id,
          period_name,
          period_start_date,
          period_end_date,
          lock_status,
          lock_type,
          description,
          created_at,
          locked_at,
          unlocked_at,
          unlock_reason,
          created_by,
          locked_by,
          unlocked_by,
          created_by_profile:user_profiles!created_by(
            full_name, 
            email
          )
        `)?.eq('organization_id', organizationId)?.order('period_end_date', { ascending: false });

      if (lockStatus) {
        query = query?.eq('lock_status', lockStatus);
      }

      if (lockType) {
        query = query?.eq('lock_type', lockType);
      }

      const { data, error } = await query;

      if (error) {
        this._handleError(error, 'fetch period locks');
      }

      return data || [];
    } catch (error) {
      this._handleError(error, 'get period locks');
    }
  },

  async createPeriodLock(organizationId, lockData) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const currentUser = await this._getCurrentUser();

      const { data, error } = await supabase?.from('period_locks')?.insert({
          ...lockData,
          organization_id: organizationId,
          created_by: currentUser?.id
        })?.select()?.single();

      if (error) {
        this._handleError(error, 'create period lock');
      }

      return data;
    } catch (error) {
      this._handleError(error, 'create period lock');
    }
  },

  // Enhanced Utility Functions - Fixed
  async getAccountingStats(organizationId) {
    try {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!supabase) {
        console.warn('Supabase client not available for stats');
        return {
          journalEntries: 0,
          pendingReviews: 0,
          activeAccounts: 0,
          recentOperations: 0
        };
      }

      // Use Promise.allSettled to handle individual query failures gracefully
      const statsPromises = [
        // Journal entries count (last 30 days)
        supabase?.from('journal_entries')?.select('id', { count: 'exact', head: true })?.eq('organization_id', organizationId)?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()),
        
        // Pending reviews count
        supabase?.from('journal_entries')?.select('id', { count: 'exact', head: true })?.eq('organization_id', organizationId)?.eq('status', 'pending_review'),
        
        // Active accounts count
        supabase?.from('chart_of_accounts')?.select('id', { count: 'exact', head: true })?.eq('organization_id', organizationId)?.eq('status', 'active'),
        
        // Recent operations count (last 7 days)
        supabase?.from('bulk_operations')?.select('id', { count: 'exact', head: true })?.eq('organization_id', organizationId)?.gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString())
      ];

      const results = await Promise.allSettled(statsPromises);

      return {
        journalEntries: results?.[0]?.status === 'fulfilled' ? (results?.[0]?.value?.count || 0) : 0,
        pendingReviews: results?.[1]?.status === 'fulfilled' ? (results?.[1]?.value?.count || 0) : 0,
        activeAccounts: results?.[2]?.status === 'fulfilled' ? (results?.[2]?.value?.count || 0) : 0,
        recentOperations: results?.[3]?.status === 'fulfilled' ? (results?.[3]?.value?.count || 0) : 0
      };
    } catch (error) {
      console.warn('Failed to get accounting stats:', error);
      return {
        journalEntries: 0,
        pendingReviews: 0,
        activeAccounts: 0,
        recentOperations: 0
      };
    }
  },

  // Enhanced Real-time subscriptions with error handling - Fixed
  subscribeToJournalEntries(organizationId, callback) {
    if (!organizationId || !callback) {
      console.warn('Organization ID and callback are required for subscription');
      return null;
    }

    if (!supabase) {
      console.warn('Supabase client not available for subscriptions');
      return null;
    }

    try {
      return supabase?.channel(`journal_entries_${organizationId}`)?.on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'journal_entries',
          filter: `organization_id=eq.${organizationId}`
        }, (payload) => {
          try {
            callback(payload);
          } catch (callbackError) {
            console.error('Journal entries subscription callback error:', callbackError);
          }
        })?.subscribe((status) => {
          console.debug('Journal entries subscription status:', status);
        });
    } catch (error) {
      console.error('Failed to create journal entries subscription:', error);
      return null;
    }
  },

  subscribeToBulkOperations(organizationId, callback) {
    if (!organizationId || !callback) {
      console.warn('Organization ID and callback are required for subscription');
      return null;
    }

    if (!supabase) {
      console.warn('Supabase client not available for subscriptions');
      return null;
    }

    try {
      return supabase?.channel(`bulk_operations_${organizationId}`)?.on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'bulk_operations',
          filter: `organization_id=eq.${organizationId}`
        }, (payload) => {
          try {
            callback(payload);
          } catch (callbackError) {
            console.error('Bulk operations subscription callback error:', callbackError);
          }
        })?.subscribe((status) => {
          console.debug('Bulk operations subscription status:', status);
        });
    } catch (error) {
      console.error('Failed to create bulk operations subscription:', error);
      return null;
    }
  }
};

// Export as both named and default for compatibility
export default accountantToolsService;